import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';
import 'firebase/compat/remote-config';
import 'firebase/compat/storage';

import { FirestoreCollectionPathTypes } from '../../models/FirestoreTypes';
import { CloudFunctionsTypes } from '../../models/FunctionsType';
import firebaseConfig from '../firebaseConfig.json';
import { EnrichCloudFunction, EnrichFirestore } from './firebaseTypes';

firebase.initializeApp(firebaseConfig);

export const isolatedFirebaseAppForAuthOnly = firebase.initializeApp(
  {
    ...firebaseConfig,
    databaseURL: undefined,
    storageBucket: undefined,
  },
  'isolated',
);

firebase.firestore().settings({ ignoreUndefinedProperties: true, merge: true });

export const firestore = () =>
  firebase.firestore() as Omit<firebase.firestore.Firestore, 'collection'> &
    EnrichFirestore<FirestoreCollectionPathTypes>;

export const fieldValueDelete = firebase.firestore.FieldValue.delete() as any;
export const fieldValueIncrement = (count: number) =>
  firebase.firestore.FieldValue.increment(count) as any;
export const serverNow =
  firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp;
export const clientNow = () => firebase.firestore.Timestamp.now();
export const timeStampFrom = (input: {
  seconds: number;
  nanoseconds: number;
}) => new firebase.firestore.Timestamp(input.seconds, input.nanoseconds);
export const timeStampFromDate = (date: Date) =>
  firebase.firestore.Timestamp.fromDate(date);
export const Timestamp = firebase.firestore.Timestamp;
export type Timestamp = firebase.firestore.Timestamp;

export const documentIdPath = firebase.firestore.FieldPath.documentId();

export const database = () => firebase.database();
export const databaseServerNow = firebase.database.ServerValue.TIMESTAMP;

export type DatabaseOnDisconnect = firebase.database.OnDisconnect;
export type ThenableReference = firebase.database.ThenableReference;
export type UploadTask = firebase.storage.UploadTask;

export const auth = firebase.auth();
export const functions = firebase.functions() as Omit<
  firebase.functions.Functions,
  'httpsCallable'
> &
  EnrichCloudFunction<CloudFunctionsTypes>;
export const storage = firebase.storage();
export const remoteConfig = firebase.remoteConfig();

export const StringFormat = firebase.storage.StringFormat;
export const firebaseFull = firebase;

// type export are only needed in this file
export type User = firebase.User;
export type AuthProvider = firebase.auth.AuthProvider;
export type ConfirmationResult = firebase.auth.ConfirmationResult;
export type IdTokenResult = firebase.auth.IdTokenResult;
export type HttpsCallableResult = firebase.functions.HttpsCallableResult;
export type DocumentSnapshot<T = any> = firebase.firestore.DocumentSnapshot<T>;
export type QuerySnapshot<T = any> = firebase.firestore.QuerySnapshot<T>;
export const DocumentSnapshot = firebase.firestore.DocumentSnapshot;
export type QueryDocumentSnapshot<T = any> =
  firebase.firestore.QueryDocumentSnapshot<T>;
export type DocumentReference<T = any> =
  firebase.firestore.DocumentReference<T>;
export type CollectionReference<T = any> = Omit<
  firebase.firestore.CollectionReference<T>,
  'doc' | 'where' | 'orderBy'
> &
  Query<T>;

type WhereValue<
  Op extends firebase.firestore.WhereFilterOp,
  Value,
> = Op extends 'in' | 'not-in' | 'array-contains-any'
  ? Value[]
  : Op extends 'array-contains'
  ? Value extends (infer ValueElement)[]
    ? ValueElement
    : Value
  : Value;

export type Query<T = firebase.firestore.DocumentData> = Omit<
  firebase.firestore.Query<T>,
  'where' | 'orderBy'
> & {
  where<Op extends firebase.firestore.WhereFilterOp>(
    fieldPath: firebase.firestore.FieldPath,
    opStr: Op,
    value: WhereValue<Op, string>,
  ): Query<T>;
  where<K extends keyof T, Op extends firebase.firestore.WhereFilterOp>(
    fieldPath: K,
    opStr: Op,
    value: WhereValue<Op, T[K]>,
  ): Query<T>;
  where<
    K extends keyof T,
    KK extends keyof T[K],
    Op extends firebase.firestore.WhereFilterOp,
  >(
    fieldPath: `${string & K}.${string & KK}`,
    opStr: Op,
    value: WhereValue<Op, T[K][KK]>,
  ): Query<T>;
  where<
    K extends keyof T,
    KK extends keyof T[K],
    KKK extends keyof T[K][KK],
    Op extends firebase.firestore.WhereFilterOp,
  >(
    fieldPath: `${string & K}.${string & KK}.${string & KKK}`,
    opStr: Op,
    value: WhereValue<Op, T[K][KK][KKK]>,
  ): Query<T>;
  orderBy(
    fieldPath: firebase.firestore.FieldPath,
    directionStr?: firebase.firestore.OrderByDirection,
  ): Query<T>;
  orderBy<K extends keyof T>(
    fieldPath: K,
    directionStr?: firebase.firestore.OrderByDirection,
  ): Query<T>;
  orderBy<K extends keyof T, KK extends keyof T[K]>(
    fieldPath: `${string & K}.${string & KK}`,
    directionStr?: firebase.firestore.OrderByDirection,
  ): Query<T>;
  orderBy<K extends keyof T, KK extends keyof T[K], KKK extends keyof T[K][KK]>(
    fieldPath: `${string & K}.${string & KK}.${string & KKK}`,
    directionStr?: firebase.firestore.OrderByDirection,
  ): Query<T>;
};
